// AdminLayout.jsx - The dashboard frame. On a laptop it is a sidebar of sections
// and a top bar; on a phone it is a title bar and a row of tabs along the bottom,
// which is where a thumb is.
//
// The spot price is in the top bar because it is the number that makes every
// offer on every screen either right or wrong, and it should never be more than
// a glance away. On a narrow screen it shortens to symbols rather than
// disappearing, because a price you cannot see is a price you assume.
//
// LEGEND
//   1. SECTIONS      the navigation, shared by the sidebar, the drawer and the tabs
//   2. Nav           the full list, for the sidebar and the drawer
//   3. Ticker        spot prices, at two widths
//   4. TabBar        bottom navigation on phones and tablets
//   5. AdminLayout   the frame

import { useEffect, useState } from "react";
import { NavLink, Outlet, Link, useLocation } from "react-router";
import clsx from "clsx";

import { useAuth } from "./AdminApp.jsx";
import { useSpot } from "../lib/hooks.js";
import { money, initials } from "../lib/format.js";
import { Seo } from "../lib/seo.jsx";
import { siteOrigin } from "../lib/host.js";
import { isStandalone } from "../lib/pwa.js";
import { Grid, Inbox, Note, Gear, Users, Menu, Close, ArrowUpRight, Refresh, Download, Truck, Scale, Tag } from "../ui/icons.jsx";
import { useUnreadTexts } from "./useUnreadTexts.js";

////////// 1. SECTIONS //////////
// `tab` marks the three that earn a place along the bottom of a phone. The rest
// are one tap further away, behind More.

const SECTIONS = [
  { to: "/admin", end: true, label: "Overview", icon: Grid, tab: true },
  { to: "/admin/orders", label: "Orders", icon: Note, tab: true },
  { to: "/admin/kits", label: "Kits", icon: Truck },
  { to: "/admin/shows", label: "Shows", icon: Scale, permission: "shows.read" },
  { to: "/admin/bounced", label: "Bounced forms", icon: Note },
  { to: "/admin/inbox", label: "Inbox", icon: Inbox, permission: "messages.read", tab: true },
  { to: "/admin/activity", label: "Activity", icon: Refresh },
  { to: "/admin/affiliates", label: "Affiliates", icon: Tag, permission: "affiliates.read" },
  { to: "/admin/team", label: "Team", icon: Users, permission: "users.read" },
  { to: "/admin/settings", label: "Settings", icon: Gear, permission: "settings.read" },
];

const SHORT = { gold: "Au", silver: "Ag", platinum: "Pt" };

////////// 2. NAV //////////

function Nav({ onNavigate }) {
  const { can, user, signOut } = useAuth();
  const unread = useUnreadTexts(can("messages.read"));

  return (
    <div className="flex flex-col h-full">
      <div className="px-5 h-[60px] flex items-center border-b border-rule shrink-0">
        <Link to="/admin" className="display text-[15px] tracking-[0.02em] text-ink" onClick={onNavigate}>
          International <span className="text-bronze">Gold</span> &amp; Silver
        </Link>
      </div>

      <nav className="flex-1 p-3 space-y-0.5 overflow-y-auto" aria-label="Dashboard sections">
        {SECTIONS.filter((section) => !section.permission || can(section.permission)).map((section) => {
          const Glyph = section.icon;
          return (
            <NavLink
              key={section.to}
              to={section.to}
              end={section.end}
              onClick={onNavigate}
              className={({ isActive }) => clsx(
                "flex items-center gap-3 px-3 py-2.5 rounded-xs text-[14px] transition-colors",
                isActive ? "bg-ink text-paper font-semibold" : "text-muted hover:bg-surface hover:text-ink"
              )}
            >
              <Glyph size={17} className="shrink-0" />
              <span className="flex-1">{section.label}</span>
              {section.to === "/admin/inbox" && unread > 0 && (
                <span
                  className="shrink-0 min-w-[1.25rem] h-5 px-1.5 rounded-full bg-bronze text-white text-[11px] font-bold flex items-center justify-center"
                  aria-label={`${unread} unread`}
                >
                  {unread > 99 ? "99+" : unread}
                </span>
              )}
            </NavLink>
          );
        })}
      </nav>

      <div className="p-3 border-t border-rule shrink-0 safe-bottom">
        {/* Installing is a settings screen rather than a banner that follows you
            around. It is a once-per-device decision, not a daily one. */}
        {can("settings.read") && !isStandalone() && (
          <Link
            to="/admin/settings?tab=device"
            onClick={onNavigate}
            className="flex items-center gap-3 px-3 py-2 text-[13px] text-muted hover:text-ink transition-colors"
          >
            <Download size={15} /> Install on this device
          </Link>
        )}

        {/* The full origin, not "/". On the admin subdomain a root link would be
            redirected straight back into the dashboard. */}
        <a
          href={siteOrigin() || "/"}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-3 px-3 py-2 text-[13px] text-muted hover:text-ink transition-colors"
        >
          <ArrowUpRight size={15} /> Open the customer site
        </a>
        <div className="flex items-center gap-3 px-3 py-2.5 mt-1 border-t border-rule-soft">
          <span className="w-7 h-7 rounded-full bg-bronze-wash text-bronze-dark text-[11px] font-bold flex items-center justify-center shrink-0">
            {initials(user.name)}
          </span>
          <span className="min-w-0 flex-1">
            <span className="block text-[13px] font-semibold text-ink truncate">{user.name}</span>
            <span className="block text-[11.5px] text-faint capitalize">{user.role}</span>
          </span>
          <button type="button" onClick={signOut} className="text-[12px] text-muted hover:text-critical transition-colors py-2">
            Sign out
          </button>
        </div>
      </div>
    </div>
  );
}

////////// 3. TICKER //////////

function Ticker({ spot }) {
  return (
    <div className="flex items-center gap-3 sm:gap-5 text-[12.5px] num whitespace-nowrap overflow-hidden">
      {["gold", "silver", "platinum"].map((metal) => (
        <span key={metal} className={clsx("items-baseline gap-1 sm:gap-1.5", metal === "platinum" ? "hidden xs:flex" : "flex")}>
          <span className="text-faint capitalize">
            <span className="sm:hidden">{SHORT[metal]}</span>
            <span className="hidden sm:inline">{metal}</span>
          </span>
          <span className={clsx("font-semibold", spot?.stale ? "text-caution" : "text-ink")}>
            {spot?.perOunce?.[metal] ? money(spot.perOunce[metal]) : "—"}
          </span>
        </span>
      ))}
      {spot?.stale && <span className="tag tag-caution hidden sm:inline-flex">Price feed stale</span>}
    </div>
  );
}

////////// 4. TAB BAR //////////

function TabBar({ onMore, moreOpen }) {
  const { can } = useAuth();
  const tabs = SECTIONS.filter((section) => section.tab && (!section.permission || can(section.permission)));

  return (
    <nav className="tabbar dashboard-chrome" aria-label="Sections">
      {tabs.map((section) => {
        const Glyph = section.icon;
        return (
          <NavLink key={section.to} to={section.to} end={section.end} className="relative">
            {({ isActive }) => (
              <span className="tabbar-link" data-active={isActive && !moreOpen ? "true" : "false"}>
                <span className="tabbar-mark" />
                <Glyph size={19} />
                {section.label}
              </span>
            )}
          </NavLink>
        );
      })}
      <button type="button" onClick={onMore} className="relative" aria-expanded={moreOpen} aria-label="More sections">
        <span className="tabbar-link" data-active={moreOpen ? "true" : "false"}>
          <span className="tabbar-mark" />
          <Menu size={19} />
          More
        </span>
      </button>
    </nav>
  );
}

////////// 5. LAYOUT //////////

export default function AdminLayout() {
  const [open, setOpen] = useState(false);
  const { spot } = useSpot({ refreshMs: 120_000 });
  const { pathname } = useLocation();

  // A drawer that survives navigation covers the page it just opened. Closing it
  // on every route change is what makes the tabs and the drawer feel like one
  // thing rather than two.
  useEffect(() => { setOpen(false); }, [pathname]);

  // The page behind a sheet must not scroll with it.
  useEffect(() => {
    if (!open) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = previous; };
  }, [open]);

  const current = SECTIONS
    .filter((section) => (section.end ? pathname === section.to : pathname.startsWith(section.to)))
    .sort((a, b) => b.to.length - a.to.length)[0];

  return (
    <div className="app-shell flex bg-paper">
      <Seo title="Dashboard" path="/admin" noIndex />

      {/* Desktop sidebar */}
      <aside className="hidden lg:flex w-[228px] shrink-0 border-r border-rule bg-surface/40 fixed inset-y-0">
        <Nav />
      </aside>

      {/* Mobile drawer, from the left, behind the More tab */}
      {open && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="absolute inset-0 bg-ink/40 fade" onClick={() => setOpen(false)} aria-hidden="true" />
          <aside className="relative w-[min(17rem,84vw)] bg-paper border-r border-rule rise safe-top overflow-y-auto">
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="btn btn-ghost absolute top-2.5 right-1.5 z-10"
              aria-label="Close menu"
            >
              <Close size={19} />
            </button>
            <Nav onNavigate={() => setOpen(false)} />
          </aside>
        </div>
      )}

      <div className="flex-1 lg:ml-[228px] min-w-0 flex flex-col">
        <header className="dashboard-chrome border-b border-rule sticky top-0 bg-paper/95 backdrop-blur-sm z-30 shrink-0 safe-top safe-x">
          <div className="h-[54px] sm:h-[60px] flex items-center gap-3 px-4 sm:px-6">
            {/* On a phone the section name replaces the sidebar: it is the only
                thing on screen that says where you are. */}
            <p className="lg:hidden display text-[16px] text-ink truncate">
              {current?.label || "Dashboard"}
            </p>

            <div className="flex-1 min-w-0" />
            <Ticker spot={spot} />
          </div>
        </header>

        <main className="flex-1 min-w-0 has-tabbar safe-x">
          <Outlet />
        </main>
      </div>

      <TabBar onMore={() => setOpen((value) => !value)} moreOpen={open} />
    </div>
  );
}
